import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PosterStripes, PosterGrain, PosterRibbon } from "@/components/PosterFX";

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 3) {
      toast.error("Give us at least a few words of context");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        message: trimmed,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });
      toast.success("Feedback sent — thanks, you're a rockstar 🚀");
      setMessage("");
      setOpen(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        toast.error("Whoa — that's a lot of feedback. Try again in a few minutes.");
      } else {
        toast.error("Couldn't send. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button — compact icon, label slides out on hover */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        data-testid="feedback-widget-trigger"
        className="fixed bottom-24 right-4 lg:bottom-5 z-40 group flex items-center gap-0 p-2 bg-[#121212]/90 backdrop-blur border border-[#27272A] hover:border-[#178AE5] rounded-full shadow-lg shadow-black/40 transition-all"
      >
        <MessageSquarePlus className="w-3.5 h-3.5 text-[#178AE5] group-hover:scale-110 transition-transform" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-[80px] group-hover:ml-1.5 transition-all duration-300 text-[10px] font-label uppercase tracking-widest text-white whitespace-nowrap">
          Feedback
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="text-white max-w-lg p-0 border-2"
          style={{
            backgroundColor: "#0A0A0A",
            borderColor: "#178AE5bb",
            backgroundImage: "linear-gradient(160deg, #178AE522, transparent 55%)",
            boxShadow: "0 0 30px #178AE555, inset 0 0 24px rgba(0,0,0,0.55)",
          }}
          data-testid="feedback-widget-dialog"
        >
          <PosterStripes color="#178AE5" size={96} opacity={0.85} />
          <PosterGrain opacity={0.06} />
          <PosterRibbon color="#178AE5" size="sm">★ TALK TO THE CORNER ★</PosterRibbon>
          <div className="relative p-4 pt-6 sm:p-5 sm:pt-7">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight uppercase">
              Send us feedback
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Bug? Idea? Something feel off? Tell us what you're thinking — we read every message.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
              }}
              placeholder="e.g. The leaderboard didn't refresh after I logged a sale... or... I love the badges, wish I could design my own..."
              rows={5}
              maxLength={4000}
              autoFocus
              className="bg-[#0A0A0A] border-[#27272A] focus:border-[#178AE5] text-white placeholder:text-[#A1A1AA]/60 resize-none"
              data-testid="feedback-widget-message"
            />
            <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
              <span>We&apos;ll include which page you were on so we can debug faster.</span>
              <span className="font-mono">{message.length} / 4000</span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={submitting}
              data-testid="feedback-widget-cancel"
              className="text-[#A1A1AA] hover:text-white hover:bg-[#0A0A0A]"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || message.trim().length < 3}
              data-testid="feedback-widget-submit"
              className="bg-[#178AE5] hover:bg-[#178AE5]/90 text-white"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <>Send feedback</>
              )}
            </Button>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
